import { useCallback, useEffect, useState } from 'react';
import type { User } from './types';

const FAVORITES_KEY = 'anime-cloud:favorites';
const WATCHED_ANIME_KEY = 'anime-cloud:watched-anime';
const WATCH_LATER_KEY = 'anime-cloud:watch-later';
const WATCHING_KEY = 'anime-cloud:watching';
const HISTORY_KEY = 'anime-cloud:history';
const SEEN_EPISODES_KEY = 'anime-cloud:seen-episodes';
const SEEN_MAP_KEY = 'anime-cloud:seen-map';
const USER_KEY = 'anime-cloud:user';

function readArray(key: string): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string').slice(0, 10_000) : [];
  } catch { return []; }
}

function readSeenMap(): Record<string, string> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(SEEN_MAP_KEY) || '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter(([episodeId, animeId]) => episodeId.length <= 200 && typeof animeId === 'string' && animeId.length <= 200));
  } catch { return {}; }
}

export function useIdStore(key: string, maxItems?: number) {
  const [ids, setIds] = useState<string[]>(() => readArray(key));
  useEffect(() => localStorage.setItem(key, JSON.stringify(ids)), [ids, key]);
  useEffect(() => {
    const sync = (event: StorageEvent) => { if (event.key === key) setIds(readArray(key)); };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [key]);
  const toggle = useCallback((id: string) => setIds((current) => {
    const next = current.includes(id) ? current.filter((x) => x !== id) : [id, ...current];
    return maxItems ? next.slice(0, maxItems) : next;
  }), [maxItems]);
  const add = useCallback((id: string) => setIds((current) => {
    const next = current.includes(id) ? [id, ...current.filter((x) => x !== id)] : [id, ...current];
    return maxItems ? next.slice(0, maxItems) : next;
  }), [maxItems]);
  const clear = useCallback(() => setIds([]), []);
  return { ids, toggle, add, clear, has: (id: string) => ids.includes(id) };
}

export const useFavorites = () => useIdStore(FAVORITES_KEY);
export const useWatchedAnime = () => useIdStore(WATCHED_ANIME_KEY);
export const useWatchLater = () => useIdStore(WATCH_LATER_KEY);
export const useWatching = () => useIdStore(WATCHING_KEY);
export const useHistory = () => useIdStore(HISTORY_KEY);
export function useSeenEpisodes() {
  const ids = useIdStore(SEEN_EPISODES_KEY);
  const [animeByEpisode, setAnimeByEpisode] = useState<Record<string, string>>(readSeenMap);
  useEffect(() => localStorage.setItem(SEEN_MAP_KEY, JSON.stringify(animeByEpisode)), [animeByEpisode]);
  useEffect(() => {
    const sync = (event: StorageEvent) => { if (event.key === SEEN_MAP_KEY) setAnimeByEpisode(readSeenMap()); };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  const add = useCallback((episodeId: string, animeId?: string) => {
    ids.add(episodeId);
    if (animeId) setAnimeByEpisode((current) => current[episodeId] === animeId ? current : { ...current, [episodeId]: animeId });
  }, [ids.add]);
  const toggle = useCallback((episodeId: string, animeId?: string) => {
    if (ids.has(episodeId)) {
      ids.toggle(episodeId);
      setAnimeByEpisode((current) => {
        const next = { ...current };
        delete next[episodeId];
        return next;
      });
    } else add(episodeId, animeId);
  }, [add, ids.has, ids.toggle]);
  const clear = useCallback(() => { ids.clear(); setAnimeByEpisode({}); }, [ids.clear]);
  return { ...ids, add, toggle, clear, animeByEpisode };
}

export function readUser(): User | null {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
}

export function saveUser(user: User | null) {
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
  window.dispatchEvent(new Event('anime-cloud:user'));
}

export function useUser() {
  const [user, setUser] = useState<User | null>(readUser);
  useEffect(() => {
    const sync = () => setUser(readUser());
    window.addEventListener('anime-cloud:user', sync);
    return () => window.removeEventListener('anime-cloud:user', sync);
  }, []);
  return user;
}
